import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import memoize from 'memoize-one';
import _ from 'lodash';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {Card} from 'primereact/card';
import {Dialog} from 'primereact/dialog';
import {Toast} from 'primereact/toast';
import {Tooltip} from 'primereact/tooltip';
import {
    Tree,
    TreeSelectionEvent,
    TreeExpandedEvent
} from 'primereact/tree';
import TreeNode from 'primereact/treenode';

import Graph from '~/frontend/src/components/base/Graph';
import ItemHistoryDialog from '~/frontend/src/components/base/dialogs/ItemHistoryDialog';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import Toolbar from '~/frontend/src/components/base/ItemFinder/detail/Toolbar';
import Headerbar from '~/frontend/src/components/base/ItemFinder/detail/Headerbar';
import ResizeableLeftMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableLeftMenuPanel';
import PropertiesPanel from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel';
import EnumPropertyPanel from '~/frontend/src/components/base/ItemFinder/detail/EnumPropertyPanel';
import ReferencePanel, {ComponentProps as ReferencePanelComponentProps} from '~/frontend/src/components/base/ItemFinder/detail/ReferencePanel';

import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import metaModel from '~/shared/src/utilities/metaModel';
import {__} from '~/shared/src/utilities/messageCatalog';
import Joi from '~/shared/src/utilities/joiExtended';
import Item from '~/shared/src/ci/Item';
import ItemType from '~/shared/src/mm/ItemType';
import User from '~/shared/src/model/User';
import {requestBaseParametersSchema} from '~/shared/src/model/BaseRequest';
import {panelSchema} from '~/shared/src/validationSchemes/panel';
import {
    rawModeSchema,
    showAdditionalRawNamesSchema
} from '~/shared/src/validationSchemes/modules/overview';
import {
    legendSchema
} from '~/shared/src/validationSchemes/modules/finder';
import messageUtils from '~/shared/src/utilities/messageUtils';
import objectUtils from '~/shared/src/utilities/objectUtils';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {
    getItemKeyDisplayName,
    getItemNameDisplayName,
    getItemTypeDisplayName,
    getPropertySetDisplayName,
    getStatusColorStyleString,
    getStatusDisplayName
} from '~/frontend/src/utilities/renderHelpers';
import {formatTimestamp} from '~/shared/src/utilities/valueFormaters';
import {SaveDataResult} from '~/shared/src/utilities/modules/data';
import StackError from '~/shared/src/base/StackError';
import {
    GetRootItemIdByDisplayAttributeResult,
    LoadDetailDataResult
} from '~/shared/src/utilities/modules/finder';
import LocalizableError from '~/shared/src/base/LocalizableError';

import * as PanelManagerActions from '~/frontend/src/actions/panelManager';
import closePanel, {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import {SetPanelDirty} from '~/frontend/src/actions/panelManager/setPanelDirty';
import {SetPanelInvalid} from '~/frontend/src/actions/panelManager/setPanelInvalid';
import {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import * as FinderActions from '~/frontend/src/actions/panel/finderDetail';
import {LoadData} from '~/frontend/src/actions/panel/finderDetail/loadData';
import saveData from '~/frontend/src/actions/panel/finderDetail/saveData';
import * as ElusiveDataActions from '~/frontend/src/actions/elusiveData';
import {
    setElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import {
    ElusiveDataState,
    PanelElusiveDataState
} from '~/frontend/src/reducers/elusiveData';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {
    MsgParams,
    UnsetMessage
} from '~/frontend/src/actions/intercom';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import cancelRequests, {CancelRequests} from '~/frontend/src/actions/ajax/cancelRequests';
import xhr from '~/frontend/src/utilities/xhr';


const Actions = Object.assign( {}, PanelManagerActions, PanelActions, FinderActions, ElusiveDataActions, IntercomActions, { cancelRequests } ) as any;
type SetParam = <K extends keyof ComponentProps>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<ComponentProps> ) => SetParamsAction;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;
type SetElusiveData = ( panelKey : string, data : PanelElusiveData ) => SetElusiveDataAction;


const referencePanelComponentProps = keys<ReferencePanelComponentProps>();


type ExpandedKeys = { [ key : string ] : true };

const defaultProps = {
    ...defaultBaseRequestParameters,

    /**
     * The ItemId to get data and metadata for.
     */
    rootItemId: 0 as number,

    readOnly: false as boolean,

    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    showHistoryPopup: false as boolean,

    /**
     * Show a legend bar.
     */
    legend: false as boolean,

    /**
     * The width of the menu on the left side in percent.
     */
    menuWidth: 15 as number,

    /**
     * The keys of the currently expanded tree nodes of the menu.
     */
    menuExpandedKeys: { enumProperties: true, references: true } as ExpandedKeys,

    /**
     * The key of the currently selected tree node of the menu.
     */
    menuSelectedKey: 'properties' as string,

    /**
     * Should tree nodes of empty enumProperties shown in the menu.
     */
    hideEmptyEnumProperties: true as boolean,

    /**
     * Should tree nodes of empty references shown in the menu.
     */
    hideEmptyReferences: true as boolean
};

type DefaultProps = typeof defaultProps;

type Props = {
    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The key of the root item. If set also rootItemType is required.
     */
    rootItemKey? : string;

    /**
     * The ItemType of the root item. If set also rootItemKey is required.
     */
    rootItemType? : string;

    /**
     * Parameters for deep linking of a property, referenced ItemType or reference property
     */
    deepLinkProps? : {
        property : string;
        referencedItemTypeName? : never;
        reference? : never;
    } | {
        property? : never;
        referencedItemTypeName : string;
        reference? : ReferencePanelComponentProps[ 'deepLinkProps' ];
    };

    /**
     * The title to display the panel with. If not set the title will be computed.
     */
    title? : string;

    /**
     * The icon to display the panel with. If not set the title will be computed.
     */
    iconCls? : string;

    /**
     * The props for sub panels
     */
    subPanelProps? : any;
};

type PanelElusiveData = PanelElusiveDataState & { showCouldNotSavePopup? : boolean };

type StateProps = {

    /**
     * The full elusiveData
     */
    allElusiveData? : ElusiveDataState;

    /**
     * The elusive data (not persited by redux store) for this component.
     * A storage for temporary needed data (not persisted to local storage via redux store persist), like
     *  - metaData.referenceCounts: The count of items for ReferenceSets.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

type ActionProps = {
    // panel manager actions
    closePanel : ClosePanel;
    setPanelInfo : SetPanelInfo;
    setPanelDirty : SetPanelDirty;
    setPanelInvalid : SetPanelInvalid;

    // common panel Actions
    setParam : SetParam;
    setParams : SetParams;
    unsetParam : UnsetParam;

    // finder actions
    loadData : LoadData;

    // elusiveData actions
    setElusiveData : SetElusiveData;

    // intercom actions
    unsetMessage : UnsetMessage;

    // xhr actions
    cancelRequests : CancelRequests;
}

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;

type State = {
    item? : Item;
    editable : boolean;
    isDirty : boolean;
    errorCount : number;
    errorTooltip? : string | JSX.Element | ( string | JSX.Element )[];
    errorMessage? : string | JSX.Element | ( string | JSX.Element )[];
    showDataNoLongerValidDialog : boolean;
};


/**
 * Finder detail
 */
class FinderDetail extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    static propertiesPanelKey = 'properties';
    static graphPanelKey = 'graph';

    static finderDetailKeySeparator = '-';

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            // abort running request
            dispatch( cancelRequests( props.uid ) );

            // clear global data
            delete global.data[ props.uid ];
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            await FinderDetail.savePanelData(
                dispatch,
                getState,
                props
            );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const result = await FinderDetail.savePanelData(
                dispatch,
                getState,
                props,
                [
                    props.uid,
                    'notification',
                    'saveSuccess',
                    'closePanel'
                ]
            );

            // no save error -> close panel
            if ( result !== false && result.success ) {
                await dispatch( closePanel( props.uid ) );
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static async savePanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<SaveDataResult | false> {
        const propsWithDefaults : DefaultProps & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid,
                  schema,
                  rootItemId
              }                                        = propsWithDefaults;

        // check for errors
        let item : Item | undefined,
            hasError : boolean = false;

        // item
        if ( rootItemId ) {
            item = global.data[ uid ]?.items?.[ rootItemId ] ?? undefined;
        }

        if ( item ) {
            const allElusiveData             = getState()?.elusiveData,
                  propertiesPanelUid         = `${uid}_${FinderDetail.propertiesPanelKey}`,
                  propertiesPanelElusiveData = ( allElusiveData?.[ propertiesPanelUid ] ?? {} ) as PanelErrorData;

            // PropertiesPanel errors
            if ( propertiesPanelElusiveData.errorData?.count ) {
                hasError = true;
            }

            // ReferencePanel errors
            else {
                const referenceSets                       = metaModel[ schema ].getReferenceSetsForItemType( item.itemTypeId ),
                      referencedItemTypeNames : string[ ] = [];

                for ( const referenceSet of referenceSets ) {
                    if ( referenceSet.srcItemTypeId === item.itemTypeId && !referencedItemTypeNames.includes( referenceSet.destItemType.name ) ) {
                        referencedItemTypeNames.push( referenceSet.destItemType.name );
                    }
                    else if ( !referencedItemTypeNames.includes( referenceSet.srcItemType.name ) ) {
                        referencedItemTypeNames.push( referenceSet.srcItemType.name );
                    }
                }

                for ( const referencedItemTypeName of referencedItemTypeNames ) {
                    const panelKey         = FinderDetail.getReferenceKey( referencedItemTypeName ),
                          panelUid         = `${uid}_${panelKey}`,
                          panelElusiveData = ( allElusiveData?.[ panelUid ] ?? {} ) as PanelErrorData;

                    if ( panelElusiveData.errorData?.count ) {
                        hasError = true;
                        break;
                    }
                }
            }
        }

        // panel has errors -> show popup
        if ( hasError ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotSavePopup: true } ) );

            return Promise.resolve( false );
        }

        // panel has no errors -> save
        else {
            return dispatch( saveData(
                uid,
                schema,
                successMsgParams
            ) );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : AllProps ) : string | undefined {
        const item = global.data[ props.uid ]?.items?.[ props.rootItemId ?? 0 ];

        let retVal : string | undefined;

        if ( item ) {
            retVal = `#${FinderDetail.panelType}/${props.schema}/${item.itemType.label}/${encodeURIComponent( item.displayAttributeValue )}`;

            // handle deep linking
            if ( props.menuSelectedKey?.match( /^enumProperty/ ) ) {
                const propertyTypeName = FinderDetail.getPropertyTypeNameFromEnumPropertyKey( props.menuSelectedKey );

                if ( propertyTypeName ) {
                    retVal += `?enumProperty=${propertyTypeName}`;
                }
            }
            else if ( props.menuSelectedKey?.match( /^reference/ ) ) {
                const itemTypeName = FinderDetail.getItemTypeNameFromReferenceKey( props.menuSelectedKey );

                if ( itemTypeName ) {
                    retVal += `?reference=${itemTypeName}`;

                    // TODO: referenceProperty
                    //  last activated?
                    /*const panelKey = FinderDetail.getReferenceKey( itemTypeName );

                    if ( props.subPanelProps?.[ panelKey ] ) {
                        console.log( props.subPanelProps?.[ panelKey ]  );
                    }*/
                }
            }
        }

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              props                            = parts?.[ 3 ]?.split( '&' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        // params[ 0 ] = panelType

        // params[ 1 ] = schema
        if ( params?.[ 1 ] ) {
            retVal.schema = params[ 1 ] as Schema;
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.finder.resolvePanelPropsFromURL.error.couldNotResolveSchema' ) );
        }

        // params[ 2 ] = rootItem.itemType.label
        let itemType : ItemType | undefined;
        if ( params?.[ 2 ] ) {
            if ( retVal.schema ) {
                itemType = metaModel[ retVal.schema ].getItemTypes().find( iT => iT.label === params[ 2 ] );
            }
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.finderDetail.resolvePanelPropsFromURL.error.couldNotResolveItemTypeName' ) );
        }

        if ( !itemType ) {
            stackError.add( new LocalizableError( 'YN.base.finder.resolvePanelPropsFromURL.error.couldNotResolveItemTypeName', { itemTypeLabel: params[ 2 ] } ) );
        }

        // params[ 3 ] = rootItem.displayAttributeValue
        if ( params?.[ 3 ] ) {
            if ( itemType && retVal.schema ) {
                const displayAttributeValue = decodeURIComponent( params[ 3 ] ),
                      res                   = await xhr.post(
                          `${BASE_URL}/api/finder/getRootItemIdByDisplayAttribute`,
                          {
                              schema    : retVal.schema,
                              itemTypeId: itemType.id,
                              key       : itemType.displayAttribute === 'key' ? displayAttributeValue : undefined,
                              name      : itemType.displayAttribute === 'key' ? undefined : displayAttributeValue
                          }
                      ),
                      result                = res?.data as GetRootItemIdByDisplayAttributeResult;

                if ( result ) {
                    if ( result.data.rootItemId ) {
                        retVal.rootItemId = result.data.rootItemId;
                    }
                    else {
                        stackError.add( new LocalizableError( 'YN.base.finderDetail.resolvePanelPropsFromURL.error.couldNotResolveRootItemId', { attribute: displayAttributeValue } ) );
                    }
                    if ( result.data.status ) {
                        retVal.status = result.data.status as Status;
                    }
                    else {
                        stackError.add( new LocalizableError( 'YN.base.finderDetail.resolvePanelPropsFromURL.error.couldNotResolveStatus' ) );
                    }
                }
            }
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.finderDetail.resolvePanelPropsFromURL.error.couldNotResolveRootItem' ) );
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        // handle deep linking
        for ( const prop of props ) {
            const propParts = prop.split( '=' );

            if ( propParts[ 0 ] === 'enumProperty' ) {
                retVal.deepLinkProps = {
                    property: propParts[ 1 ]
                };
            }
            else if ( propParts[ 0 ] === 'reference' ) {
                retVal.deepLinkProps = {
                    referencedItemTypeName: propParts[ 1 ]
                };
                /*
                // reference properties (addition to referencedItemTypeName)
                retVal.deepLinkProps.reference = {
                    referenceId      : 'referenceId',
                    referenceProperty: 'propertyTypeName'
                };*/
            }
        }

        return retVal;
    }

    static getEnumPropertyKey( propertyTypeName : string ) : string {
        return `enumProperty${FinderDetail.finderDetailKeySeparator}${propertyTypeName}`;
    }

    static getPropertyTypeNameFromEnumPropertyKey( key : string ) : string {
        const parts = key.split( FinderDetail.finderDetailKeySeparator );

        return parts?.[ 1 ];
    }

    static getReferenceKey( itemTypeName : string ) : string {
        return `reference${FinderDetail.finderDetailKeySeparator}${itemTypeName}`;
    }

    static getItemTypeNameFromReferenceKey( key : string ) : string {
        const parts = key.split( FinderDetail.finderDetailKeySeparator );

        return parts?.[ 1 ];
    }

    // panel manager config
    static panelType = 'FinderDetail';
    static panelTabTitle = 'Finder';
    static panelTabIcon = 'pi pi-search-plus';

    static panelPropsSchemaMsgIdPrefix = 'YN.base.finder';
    static panelPropsSchema = Joi.object()
        .concat( panelSchema )
        .concat( Joi.object( {
            // panelSchema defaults
            panelType    : Joi.string().valid( FinderDetail.panelType ),
            panelTabTitle: Joi.string().default( FinderDetail.panelTabTitle ),
            panelTabIcon : Joi.string().default( FinderDetail.panelTabIcon )
        } ) )
        .concat( requestBaseParametersSchema )
        .concat( Joi.object()
            .keys( {
                rootItemId  : Joi.number(),
                rootItemKey : Joi.string(),
                rootItemType: Joi.string()
            } )
            .xor( 'rootItemId', 'rootItemKey' )
            .xor( 'rootItemId', 'rootItemType' )
        )
        .concat( Joi.object( {
            rawMode               : rawModeSchema,
            showAdditionalRawNames: showAdditionalRawNamesSchema,
            legend                : legendSchema,
            //
            menuWidth              : Joi.number()
                .default( 15 )
                .description( 'The width of the left menu panel in percent of the available width.' ),
            menuSelectedKey        : Joi.alternatives()
                .try(
                    false,
                    Joi.string()
                )
                .default( false )
                .description( 'The key of the node to select in the left menu panel.' ),
            menuExpandedKeys       : Joi.object( {
                enumProperties: Joi.boolean()
                    .description( 'Should the enumProperties tree node be expanded.' ),
                references    : Joi.boolean()
                    .description( 'Should the references tree node be expanded.' )
            } ),
            hideEmptyEnumProperties: Joi.boolean()
                .default( true )
                .description( 'Should tree nodes of empty enumProperties shown in the menu.' ),
            hideEmptyReferences    : Joi.boolean()
                .default( true )
                .description( 'Should tree nodes of empty references shown in the menu.' ),
            deepLinkProps          : Joi.alternatives()
                .try(
                    Joi.object( {
                        property: Joi.string()
                            .required()
                            .description( 'The PropertyTypeName of property to show. Only useful for enumerated properties.' )
                    } ),
                    Joi.object( {
                        referencedItemTypeName: Joi.string()
                            .required()
                            .description( 'The ItemTypeName of references to show.' ),
                        reference             : Joi.object( {
                            referenceId      : Joi.number()
                                .required()
                                .description( 'The Id of the reference to show a property for.' ),
                            referenceProperty: Joi.string()
                                .required()
                                .description( 'The PropertyTypeName of referenceProperty to show.' )
                        } )
                    } )
                )
                .description( 'Parameters for deep linking.' )
        } ) );


    /* properties *************************************************************************************************************************/

    toast? : Toast | null;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        // init state
        this.state = {
            editable                   : !props.readOnly,
            isDirty                    : false,
            errorCount                 : 0,
            showDataNoLongerValidDialog: false
        };

        // memoize some methods for performance
        this.checkForIntercomMessages = memoize( this.checkForIntercomMessages );
        this.setPanelInfo = memoize( this.setPanelInfo );
        this.getMenu = memoize( this.getMenu );

        // bind event handlers
        this.onMenuWidthChange = this.onMenuWidthChange.bind( this );
        this.onMenuToggle = this.onMenuToggle.bind( this );
        this.onMenuSelectionChange = this.onMenuSelectionChange.bind( this );
        this.onHideEmptyEnumProperties = this.onHideEmptyEnumProperties.bind( this );
        this.onHideEmptyReferences = this.onHideEmptyReferences.bind( this );

        this.processDeepLink = this.processDeepLink.bind( this );
        this.menuNodeTemplate = this.menuNodeTemplate.bind( this );
        this.menuActionNodeTemplate = this.menuActionNodeTemplate.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  allElusiveData,
                  readOnly,
                  rootItemId,
                  schema,
                  timestamp,
                  uid,
                  user
              }                       = props,
              retVal : Partial<State> = {
                  editable    : false,
                  isDirty     : false,
                  errorCount  : 0,
                  errorTooltip: undefined,
                  errorMessage: undefined
              };

        // item
        if ( rootItemId ) {
            retVal.item = global.data[ uid ]?.items?.[ rootItemId ] ?? undefined;
        }

        // editable / is dirty / errors
        if ( !readOnly && retVal.item ) {
            const itemTypeId                                 = retVal.item.itemTypeId,
                  editableStatusIds                          = metaModel[ schema ].getEditableStatus().map( editStatus => editStatus.id ),
                  propertiesPanelUid                         = `${uid}_${FinderDetail.propertiesPanelKey}`,
                  propertiesPanelElusiveData                 = ( allElusiveData?.[ propertiesPanelUid ] ?? {} ) as PanelErrorData,
                  referenceSets                              = metaModel[ schema ].getReferenceSetsForItemType( retVal.item.itemTypeId ),
                  referencedItemTypeNames : string[]         = [],
                  tooltipErrors : ( string | JSX.Element )[] = [],
                  messageErrors : ( string | JSX.Element )[] = [];

            // editable
            if (
                itemTypeId > 0
                && user?.hasPermissionOnItemType( 'update', itemTypeId, schema )
                && user?.hasGenericPermissionOnItemType( 'nonBlocked', itemTypeId, schema )
                && timestamp === 'now'
                && editableStatusIds.includes( retVal.item.statusId )
            ) {
                retVal.editable = true;
            }

            // is dirty
            retVal.isDirty = retVal.item.isModified() || retVal.item.isNew();

            // check if one of the item references is dirty (modified or new)
            if ( !retVal.isDirty && retVal.item.references.length ) {
                for ( const referenceId of retVal.item.references ) {
                    const reference = global.data[ uid ].references[ referenceId ];

                    retVal.isDirty = !!retVal.isDirty || reference.isNew() || reference.isModified() || ( reference.srcItemId === rootItemId ? reference.destItem?.isModified() : reference.srcItem?.isModified() )

                    if ( retVal.isDirty ) {
                        break;
                    }
                }
            }

            // errors
            // PropertiesPanel errors
            if ( propertiesPanelElusiveData.errorData?.count ) {
                retVal.errorCount! += propertiesPanelElusiveData.errorData.count;

                if ( propertiesPanelElusiveData.errorData.tooltip?.length ) {
                    tooltipErrors.push(
                        <>
                            <div className="yn-tooltip-header2">
                                {__( 'YN.base.finder.detail.propertiesPanel.error.tooltip.title' )}
                            </div>
                            {propertiesPanelElusiveData.errorData.tooltip}
                        </>
                    );
                }

                if ( propertiesPanelElusiveData.errorData.message?.length ) {
                    messageErrors.push(
                        <>
                            <div className="yn-error-subtitle">
                                {__( 'YN.base.finder.detail.propertiesPanel.error.message.title' )}
                            </div>
                            <hr className="yn-error-ruler"/>
                            <div className="yn-error-suberror">
                                {propertiesPanelElusiveData.errorData.message}
                            </div>
                        </>
                    );
                }
            }
            // ReferencePanel errors
            for ( const referenceSet of referenceSets ) {
                if ( referenceSet.srcItemTypeId === retVal.item.itemTypeId && !referencedItemTypeNames.includes( referenceSet.destItemType.name ) ) {
                    referencedItemTypeNames.push( referenceSet.destItemType.name );
                }
                else if ( !referencedItemTypeNames.includes( referenceSet.srcItemType.name ) ) {
                    referencedItemTypeNames.push( referenceSet.srcItemType.name );
                }
            }
            for ( const referencedItemTypeName of referencedItemTypeNames ) {
                const panelKey         = FinderDetail.getReferenceKey( referencedItemTypeName ),
                      panelUid         = `${uid}_${panelKey}`,
                      panelElusiveData = ( allElusiveData?.[ panelUid ] ?? {} ) as PanelErrorData;

                if ( panelElusiveData.errorData?.count ) {
                    retVal.errorCount! += panelElusiveData.errorData.count;

                    if ( panelElusiveData.errorData.tooltip?.length ) {
                        tooltipErrors.push(
                            <>
                                <div className="yn-tooltip-header2">
                                    {__( 'YN.base.finder.detail.referencePanel.error.tooltip.title', { key: referencedItemTypeName } )}
                                </div>
                                {panelElusiveData.errorData.tooltip}
                            </>
                        );
                    }

                    if ( panelElusiveData.errorData.message?.length ) {
                        messageErrors.push(
                            <>
                                <div className="yn-error-subtitle">
                                    {__( 'YN.base.finder.detail.referencePanel.error.message.title', { key: referencedItemTypeName } )}
                                </div>
                                <hr className="yn-error-ruler"/>
                                <div className="yn-error-suberror">
                                    {panelElusiveData.errorData.message}
                                </div>
                            </>
                        );
                    }
                }
            }

            if ( retVal.errorCount ) {
                retVal.errorTooltip = <div key="error" className="yn-tooltip-error">
                    <div className="yn-tooltip-header">
                        {__( 'YN.base.frontend.dataTable.tooltip.error.title' )}
                    </div>
                    {tooltipErrors}
                </div>;

                retVal.errorMessage = <>
                    <div className="yn-error-message">
                        {__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.text' )}
                    </div>
                    <hr className="yn-error-ruler"/>
                    <div className="yn-error-suberror">
                        {messageErrors}
                    </div>
                </>;
            }
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.afterRender();
        this.init();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : ComponentProps ) {
        this.afterRender();
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        // abort running request
        this.props.cancelRequests( this.props.uid );
    }

    /**
     * REDUX stuff
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        const changedProps = objectUtils.getDiffKeys(
            this.props,
            nextProps,
            [
                'allElusiveData',
                'deepLinkProps',
                'elusiveData',
                'intercomMessages',
                'subPanelProps',
                'user'
            ]
        );

        let retVal : boolean = false;

        const reloadForcingProps = [
                  'status',
                  'timestamp'
              ],
              reload             = changedProps.some( prop => reloadForcingProps.includes( prop ) );

        // TODO: resolve this via actions (detailSetStatus, detailSetTimestamp)
        if ( reload && nextProps.rootItemId > 0 ) {
            this.loadDetail( nextProps );
        }
        else {
            retVal = true;
        }

        return retVal;
    }

    /* event handlers *********************************************************************************************************************/

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenuWidthChange( width : number ) {
        this.props.setParam( this.props.uid, 'menuWidth', width );
    }

    /**
     * Handler for menu toggle.
     *
     * @private
     */
    onMenuToggle( params : TreeExpandedEvent ) {
        this.props.setParam( this.props.uid, 'menuExpandedKeys', params.value as ExpandedKeys );
    }

    /**
     * Handler for menu selection change.
     *
     * @private
     */
    onMenuSelectionChange( params : TreeSelectionEvent ) {
        const {
                  uid,
                  subPanelProps
              }               = this.props,
              menuSelectedKey = params.value as unknown as string;

        if ( menuSelectedKey?.match( /^reference/ ) ) {
            const itemTypeName = FinderDetail.getItemTypeNameFromReferenceKey( menuSelectedKey );

            if ( itemTypeName ) {
                const panelKey = FinderDetail.getReferenceKey( itemTypeName ),
                      panelUid = `${uid}_${panelKey}`;

                if ( !subPanelProps?.[ panelKey ] ) {
                    const panelProps = this.initializeReferencePanelProps( { uid: panelUid, itemTypeName } );

                    this.props.setParams( panelUid, panelProps );
                }
            }
        }

        this.props.setParam( uid, 'menuSelectedKey', menuSelectedKey );
    }

    /**
     * Handler for menu enumProperties tree node hideEmptyButton toggle.
     *
     * @private
     */
    onHideEmptyEnumProperties( hidden : boolean ) {
        this.props.setParam( this.props.uid, 'hideEmptyEnumProperties', hidden );
    }

    /**
     * Handler for menu references tree node hideEmptyButton toggle.
     *
     * @private
     */
    onHideEmptyReferences( hidden : boolean ) {
        this.props.setParam( this.props.uid, 'hideEmptyReferences', hidden );
    }

    /* private ****************************************************************************************************************************/

    afterRender() {
        this.checkForIntercomMessages( this.props.intercomMessages );

        this.setPanelInfo(
            this.props.uid,
            this.props.title,
            this.props.iconCls,
            this.props.schema,
            this.props.status,
            this.props.timezone,
            this.props.timestamp,
            this.props.rootItemId,
            this.props.rawMode,
            this.props.showAdditionalRawNames,
            this.state.isDirty,
            this.state.errorCount,
            this.props.elusiveData?.data?.items?.[ this.props.rootItemId ] ?? 0,
            FinderDetail.buildPanelPathFromProps( this.props )
        );
    }

    /**
     * Sets the title and icon for the tab of this component.
     *
     * @private
     */
    setPanelInfo( uid : string, panelTitle : string | undefined, panelIconCls : string | undefined, schema : Schema, status : Status, timezone : string, timestamp : string, rootItemId : number, rawMode : boolean, showAdditionalRawNames : boolean, isDirty : boolean, errorCount : number, rootItemRevision : number, path? : string ) {
        const item = global.data[ uid ]?.items?.[ rootItemId ];

        let title            = __( panelTitle ?? FinderDetail.panelTabTitle ),
            icon             = panelIconCls ?? FinderDetail.panelTabIcon,
            tooltip : string = '';

        if ( item ) {
            const itemLabel       = item.displayAttributeValue,
                  itemTypeLabel   = getItemTypeDisplayName( item.itemTypeId, schema, rawMode, showAdditionalRawNames ),
                  itemStatusStyle = getStatusColorStyleString( item.statusId, schema ),
                  itemId          = item.id > 0 ? item.id : 'NEW',
                  itemTypeIcon    = item.itemType.icon;

            if ( !panelTitle ) {
                title = `${itemLabel} (${itemId})`;
            }
            else {
                const labelTemplateResolver = messageUtils.getMessageTemplateStringResolver( item );

                title = labelTemplateResolver( __( title ?? '' ) );
            }

            if ( !panelIconCls && itemTypeIcon ) {
                icon = `yn-tab-image-icon icon-itemtype-detail ${itemTypeIcon}`;
            }

            tooltip += `<div className="yn-tooltip-header">${__( 'YN.base.finder.detail.tooltip.title', { itemLabel: `<span ${itemStatusStyle}>${itemLabel}</span>` } )}</div>`;

            // error
            if ( errorCount ) {
                tooltip += `<div className="yn-tooltip-info mb-0">
                            <div className="yn-tooltip-info-title"><span className="fas fa-exclamation-circle yn-error mr-2"></span> ${__( 'YN.base.panel.tooltip.hasError' )}</div>
                        </div>`;
            }

            // dirty
            else if ( isDirty ) {
                tooltip += `<div className="yn-tooltip-info mb-0">
                            <div className="yn-tooltip-info-title"><span className="fas fa-exclamation-triangle yn-dirty mr-2"></span> ${__( 'YN.base.panel.tooltip.isDirty' )}</div>
                        </div>`;
            }

            // item information
            tooltip += `<div className="yn-tooltip-info">`;
            tooltip += `<div className="yn-tooltip-info-row">`;
            tooltip += `<div className="yn-tooltip-info-content flex-1">
                                <div className="yn-tooltip-info-content-label">${getItemKeyDisplayName( item.itemTypeId, schema, rawMode, showAdditionalRawNames )}</div>
                                <div className="yn-tooltip-info-content-text" style="font-size: 1.25rem; white-space: nowrap;">${item.key}</div>
                            </div>`;
            tooltip += `<div className="yn-tooltip-info-content flex-1">
                                <div className="yn-tooltip-info-content-label">${getItemNameDisplayName( item.itemTypeId, schema, rawMode, showAdditionalRawNames )}</div>
                                <div className="yn-tooltip-info-content-text" style="font-size: 1.25rem; white-space: nowrap;">${item.name}</div>
                            </div>`;
            tooltip += `<div className="yn-tooltip-info-content flex-none" style="width: 4rem; text-align: right;">
                                <div className="yn-tooltip-info-content-label">${__( 'YN.base.finder.item.idLabel' )}</div>
                                <div className="yn-tooltip-info-content-text" style="font-size: 1.25rem;">${itemId}</div>
                            </div>`;
            tooltip += `</div>`;
            tooltip += `<div className="yn-tooltip-info-row">`;
            tooltip += `<div className="yn-tooltip-info-content flex-none" style="width: calc( 50% - 3rem );">
                                <div className="yn-tooltip-info-content-label">${__( 'YN.base.finder.item.typeLabel' )}</div>
                                <div className="yn-tooltip-info-content-text">${itemTypeLabel}</div>
                            </div>`;
            tooltip += `<div className="yn-tooltip-info-content flex-1">
                                <div className="yn-tooltip-info-content-label">${__( 'YN.base.finder.status' )}</div>
                                <div className="yn-tooltip-info-content-text"><span ${itemStatusStyle}>${getStatusDisplayName( item.statusId, schema, rawMode, showAdditionalRawNames )}</span></div>
                            </div>`;
            tooltip += `</div>`;
            tooltip += '</div>';

            // adv search options
            if ( status !== 'active' || timestamp !== 'now' ) {
                tooltip += `<div className="yn-tooltip-info">
                                <div className="yn-tooltip-info-title">${__( 'YN.base.finder.tooltip.advSearchOptions' )}</div>`;
            }
            // status
            if ( status !== 'active' ) {
                let statusLabel = '';

                switch ( status ) {
                    case 'all-non-history':
                        statusLabel = __( 'YN.base.status.allNonHistory' );
                        break;
                    case 'all':
                        statusLabel = __( 'YN.base.status.all' );
                        break;
                }

                tooltip += `<div className="yn-tooltip-info-content">
                                <div className="yn-tooltip-info-content-label">${__( 'YN.base.finder.status.label' )}</div>
                                <div className="yn-tooltip-info-content-text">${statusLabel}</div>
                            </div>`;
            }
            // timestamp
            if ( timestamp !== 'now' ) {
                tooltip += `<div className="yn-tooltip-info-content">
                                <div className="yn-tooltip-info-content-label">${__( 'YN.base.finder.timestamp.label' )}</div>
                                <div className="yn-tooltip-info-content-text">${formatTimestamp( timestamp, timezone )}</div>
                            </div>`;
            }
            if ( status !== 'active' || timestamp !== 'now' ) {
                tooltip += `</div>`;
            }
        }

        this.props.setPanelInfo( this.props.uid, { title, icon, tooltip, dirty: isDirty, invalid: !!errorCount, path } );
    }

    /**
     * Processes given deepLinkProps.
     *
     * @private
     */
    processDeepLink() {
        if ( this.props.deepLinkProps ) {
            const {
                      uid,
                      rootItemId,
                      deepLinkProps
                  }    = this.props,
                  item = global.data[ uid ]?.items?.[ rootItemId ] ?? undefined;

            let clearDeepLinkProps = true,
                menuSelectedKey : string | undefined;

            if ( item ) {
                // property
                if ( deepLinkProps.property ) {
                    const propertySet = metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( item.itemTypeId, deepLinkProps.property );

                    // EnumProperty
                    if ( propertySet?.isEnumerated ) {
                        menuSelectedKey = FinderDetail.getEnumPropertyKey( propertySet.name );
                    }
                }

                // reference
                else if ( deepLinkProps.referencedItemTypeName ) {
                    const refPanelKey = FinderDetail.getReferenceKey( deepLinkProps.referencedItemTypeName );

                    menuSelectedKey = refPanelKey;

                    if ( deepLinkProps.reference ) {
                        clearDeepLinkProps = false;
                    }
                }
            }

            if ( menuSelectedKey ) {
                this.props.setParam( uid, 'menuSelectedKey', menuSelectedKey );
            }

            if ( clearDeepLinkProps ) {
                this.props.unsetParam( uid, 'deepLinkProps' );
            }
        }
    }

    /**
     * Init
     *
     * @private
     */
    init() {
        // no data given
        if ( !global.data[ this.props.uid ]?.items?.[ this.props.rootItemId ] ) {
            // new item id -> error
            if ( this.props.rootItemId < 0 ) {
                this.setState( { showDataNoLongerValidDialog: true } );
            }

            // real item id -> load
            else if ( this.props.rootItemId > 0 || ( this.props.rootItemType && this.props.rootItemKey ) ) {
                this.loadDetail();
            }
        }
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        let createMsgHandled = false;

        // check for notifications
        if ( intercomMessages?.notification ) {
            for ( const notification of Object.keys( intercomMessages.notification ) ) {
                // update rootItemId after successful save
                if ( notification === 'saveSuccess' && this.props.rootItemId ) {
                    const newIdMapping = intercomMessages.notification[ notification ],
                          rootItemId   = newIdMapping.itemMetaDataMapping?.[ this.props.rootItemId ]?.id;

                    if ( rootItemId && rootItemId !== this.props.rootItemId ) {
                        this.props.setParam( this.props.uid, 'rootItemId', rootItemId );

                        this.loadDetail( {
                            ...this.props,
                            rootItemId
                        } );

                        createMsgHandled = true;
                    }
                }

                // clear notification message
                this.props.unsetMessage( this.props.uid, 'notification', notification as string );
            }
        }

        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );

            for ( const toastKey of toastKeys ) {
                // show toast
                if ( toastKey === 'saveSuccess' && !createMsgHandled ) {
                    this.loadDetail();
                }

                // force reload after successful delete
                else if ( toastKey === 'deleteSuccess' ) {
                    this.props.setParam( this.props.uid, 'status', 'all' );
                    this.props.setParam( this.props.uid, 'timestamp', 'now' );

                    this.loadDetail( {
                        ...this.props,
                        status   : 'all',
                        timestamp: 'now'
                    } );
                }

                // show toast
                this.toast.show( intercomMessages.toast[ toastKey ] );

                // clear toast message
                this.props.unsetMessage( this.props.uid, 'toast', toastKey as string );
            }
        }
    }

    /**
     * load the item detail data from the backend
     *
     * @private
     */
    loadDetail( props : AllProps = this.props as AllProps ) {
        this.props.loadData(
            props.uid,
            props.schema,
            props.status,
            props.timestamp,
            props.timezone,
            props.rootItemId,
            props.rootItemId === 0 ? props.rootItemType : undefined,
            props.rootItemId === 0 ? props.rootItemKey : undefined,
            ( result : LoadDetailDataResult ) => {
                if ( props.rootItemId === 0 && result.data.items?.[ 0 ]?.id ) {
                    this.props.setParam( this.props.uid, 'rootItemId', result.data.items[ 0 ].id );
                }

                this.processDeepLink();
            }
        );
    }

    initializeReferencePanelProps( panelProps : PanelProps ) : PanelProps {
        let retVal : PanelProps = {};

        if ( this.state.item && this.props.user ) {
            retVal = ReferencePanel.onPreInitializePanel?.( {
                ..._.pick( this.props, referencePanelComponentProps ) as ReferencePanelComponentProps,
                ...panelProps,
                parentItemId      : this.state.item.id,
                parentItemTypeName: this.state.item.itemType.name,
                user              : this.props.user
            } );

            retVal = ReferencePanel.onInitializePanel?.( {
                ...retVal as ReferencePanelComponentProps,
                user: this.props.user
            } );
        }

        return retVal;
    }

    /**
     * Template for tree nodes.
     *
     * @private
     */
    menuNodeTemplate( node : TreeNode.TreeNode ) : string | JSX.Element[] | undefined {
        let retVal : string | JSX.Element[] | undefined = this.menuActionNodeTemplate( node );

        if ( retVal === node.label ) {
            if ( node.data?.tooltip || node.data?.count ) {
                retVal = [
                    <span key="label" className="yn-label" id={`${node.key}_error`}>{node.label}</span>
                ];
                if ( node.data?.tooltip ) {
                    retVal.unshift(
                        <Tooltip
                            key={`#${node.key}_error_tooltip`}
                            target={`#${node.key}_error`}
                            position="right"
                            autoHide={false}
                            className="yn-tooltip"
                        >
                            {node.data.tooltip}
                        </Tooltip>
                    );
                }
                if ( node.data?.count ) {
                    retVal.push(
                        <span key="count" className="p-badge p-badge-no-gutter">
                            {node.data.count}
                        </span>
                    );
                }
            }
        }

        return retVal;
    }

    /**
     * Template for action tree nodes.
     *
     * @private
     */
    menuActionNodeTemplate( node : TreeNode.TreeNode ) : string | JSX.Element[] | undefined {
        let retVal : string | JSX.Element[] | undefined = node.label,
            hasAction : boolean                         = false,
            iconName : string                           = '';

        const actions : JSX.Element[] = [];

        if ( node.data ) {
            if ( node.data.showHideEmptyToggle ) {
                hasAction = true;

                const hidden  = !!node.data.hideEmpty,
                      handler = node.data.hideEmptyToggleHandler;

                if ( hidden ) {
                    iconName = 'pi-eye-slash';
                }
                else {
                    iconName = 'pi-eye';
                }

                actions.push(
                    <Button
                        key="node_action_hideEmptyToggle"
                        icon={`pi ${iconName}`}
                        className={`p-button-text p-button-rounded`}
                        onClick={() => {
                            handler( !hidden );
                        }}
                    />
                );
            }
        }

        if ( hasAction ) {
            retVal = [
                <div key="node_label" className="yn-action-node-label">{node.label}</div>
            ].concat( actions );
        }

        return retVal;
    }

    /**
     * Builds and returns the menu.
     *
     * @param schema
     * @param item
     * @param allElusiveData
     * @param elusiveData
     * @param menuExpandedKeys
     * @param menuSelectedKey
     * @param hideEmptyEnumProperties
     * @param hideEmptyReferences
     *
     * @private
     */
    getMenu( schema : Schema, item : Item | undefined, allElusiveData : ElusiveDataState | undefined, elusiveData : PanelElusiveDataState | undefined, menuExpandedKeys : ExpandedKeys, menuSelectedKey : string | undefined, hideEmptyEnumProperties : boolean, hideEmptyReferences : boolean, rawMode : boolean, showAdditionalRawNames : boolean ) : JSX.Element {
        const enumPropertiesTreeData : TreeNode.TreeNode[] = [],
              referencesTreeData : TreeNode.TreeNode[]     = [],
              referencesNodeClassNames : string[]          = [ 'yn-action-node' ];

        let itemOrPropertiesModified : boolean = false,
            propertiesPanelClassName : string  = '',
            propertiesPanelTooltip : string | JSX.Element | ( string | JSX.Element )[] | undefined,
            referencesHasInvalid : boolean     = false,
            referencesHasDirty : boolean       = false;

        if ( item ) {
            const {
                      uid
                  }                                                       = this.props,
                  propertiesPanelUid                                      = `${uid}_${FinderDetail.propertiesPanelKey}`,
                  propertiesPanelElusiveData                              = ( allElusiveData?.[ propertiesPanelUid ] ?? {} ) as PanelErrorData,
                  propertySets                                            = item.itemType.propertySets,
                  referenceSets                                           = item.itemType.referenceSets,
                  referencedItemTypes : { [ itemTypeId : number ] : any } = {},
                  circularReferenceSetsHandled : number[]                 = [],
                  deletedStatusIds                                        = metaModel[ schema ].getDeletedStatus().map( s => s.id );

            let itemType : ItemType;

            // check if item or one of its properties is modified
            itemOrPropertiesModified = !!item.attributesModified.size;
            if ( !itemOrPropertiesModified && item.properties.size ) {
                const propertiesIterator = item.properties.values();
                let value, done;
                do {
                    ( { value, done } = propertiesIterator.next() );

                    if ( !done ) {
                        itemOrPropertiesModified = itemOrPropertiesModified || value.isModified() || value.isNew();
                    }
                }
                while ( !itemOrPropertiesModified && !done )
            }
            if ( itemOrPropertiesModified ) {
                propertiesPanelClassName = 'yn-modified';
            }

            // check if item has error
            if ( propertiesPanelElusiveData.errorData?.count ) {
                propertiesPanelClassName = 'yn-error';
                propertiesPanelTooltip = <div key="error" className="yn-tooltip-error">
                    <div className="yn-tooltip-header">
                        {__( 'YN.base.frontend.dataTable.tooltip.error.title' )}
                    </div>
                    {propertiesPanelElusiveData.errorData.tooltip}
                </div>;
            }

            // get count and modified for enumerated properties
            for ( const propertySet of propertySets ) {
                if ( propertySet.isEnumerated ) {
                    const key          = FinderDetail.getEnumPropertyKey( propertySet.name ),
                          enumProperty = item.enumProperties.get( propertySet.propertyTypeId ),
                          modified     = ( enumProperty?.isNew() ?? false ) || ( enumProperty?.isModified() ?? false );

                    let count = enumProperty?.properties.size ?? 0;

                    if ( count ) {
                        enumProperty?.properties.forEach( property => {
                            if ( this.props.status !== 'all' && deletedStatusIds.includes( property.statusId ) ) {
                                count--;
                            }
                        } );

                        enumPropertiesTreeData.push( {
                            key      : key,
                            label    : getPropertySetDisplayName( propertySet.id, propertySet.schema, rawMode, showAdditionalRawNames ),
                            data     : {
                                count
                            },
                            children : [],
                            className: modified ? 'yn-modified' : ''
                        } );
                    }
                    else if ( !hideEmptyEnumProperties ) {
                        enumPropertiesTreeData.push( {
                            key      : key,
                            label    : propertySet.label,
                            className: 'yn-empty-node',
                            children : []
                        } );
                    }

                }
            }
            enumPropertiesTreeData.sort( ( value1, value2 ) => {
                if ( value1.label && value2.label ) {
                    if ( value1.label > value2.label ) {
                        return 1;
                    }
                    else if ( value1.label < value2.label ) {
                        return -1;
                    }
                }

                return 0;
            } );

            // get reference counts from loaded metaData
            for ( const referenceSet of referenceSets ) {
                if ( referenceSet.srcItemTypeId === item.itemTypeId ) {
                    itemType = referenceSet.destItemType;
                }
                else {
                    itemType = referenceSet.srcItemType;
                }

                const key      = FinderDetail.getReferenceKey( itemType.name ),
                      refCount = ( elusiveData?.metaData?.referenceCounts?.dest?.[ referenceSet.id ] ?? 0 ) + ( elusiveData?.metaData?.referenceCounts?.src?.[ referenceSet.id ] ?? 0 ),
                      count    = !circularReferenceSetsHandled.includes( referenceSet.id )
                          ? refCount
                          : 0;

                if ( referenceSet.srcItemTypeId === referenceSet.destItemTypeId && !circularReferenceSetsHandled.includes( referenceSet.id ) ) {
                    circularReferenceSetsHandled.push( referenceSet.id );
                }

                if ( !referencedItemTypes[ itemType.id ] ) {
                    referencedItemTypes[ itemType.id ] = {
                        key,
                        label   : getItemTypeDisplayName( itemType.id, itemType.schema, rawMode, showAdditionalRawNames ),
                        count,
                        modified: false
                    };
                }
                else {
                    referencedItemTypes[ itemType.id ].count += count;
                }
            }
            // adjust count and get modified for references from loaded ones
            for ( const referenceId of item.references ) {
                const reference               = global.data[ this.props.uid ].references[ referenceId ],
                      modified                = reference.isNew() || reference.isModified() || ( reference.srcItemId === item.id ? reference.destItem?.isModified() : reference.srcItem?.isModified() ),
                      referenceTargetItemType = reference.srcItemId === item.id ? reference.referenceSet.destItemType : reference.referenceSet.srcItemType;

                let isDeleted = false;

                if ( this.props.status !== 'all' && deletedStatusIds.includes( reference.statusId ) ) {
                    isDeleted = true;
                }

                if ( referencedItemTypes[ referenceTargetItemType.id ] ) {
                    referencedItemTypes[ referenceTargetItemType.id ].count -= isDeleted ? 1 : 0;
                    referencedItemTypes[ referenceTargetItemType.id ].modified = referencedItemTypes[ referenceTargetItemType.id ].modified || modified;
                }
            }
            for ( const referencedItemTypeId in referencedItemTypes ) {
                const referencedItemType = referencedItemTypes[ referencedItemTypeId ],
                      panelUid           = `${uid}_${referencedItemType.key}`,
                      panelElusiveData   = ( allElusiveData?.[ panelUid ] ?? {} ) as PanelErrorData,
                      panelErrorCount    = panelElusiveData.errorData?.count;

                let className : string = '',
                    tooltip : string | JSX.Element | ( string | JSX.Element )[] | undefined;

                if ( panelErrorCount ) {
                    if ( panelElusiveData.errorData?.tooltip?.length ) {
                        tooltip = <div key="error" className="yn-tooltip-error">
                            <div className="yn-tooltip-header">
                                {__( 'YN.base.frontend.dataTable.tooltip.error.title' )}
                            </div>
                            {panelElusiveData.errorData.tooltip}
                        </div>;
                    }
                }

                if ( referencedItemType.count ) {
                    if ( panelErrorCount ) {
                        className = 'yn-error';
                        referencesHasInvalid = true;
                    }
                    else if ( referencedItemType.modified ) {
                        className = 'yn-modified';
                        referencesHasDirty = true;
                    }

                    referencesTreeData.push( {
                        key     : referencedItemType.key,
                        label   : referencedItemType.label,
                        className,
                        data    : {
                            count: referencedItemType.count,
                            tooltip
                        },
                        children: []
                    } );
                }
                else if ( !hideEmptyReferences ) {
                    referencesTreeData.push( {
                        key      : referencedItemType.key,
                        label    : referencedItemType.label,
                        className: 'yn-empty-node',
                        children : []
                    } );
                }
            }
            referencesTreeData.sort( ( value1, value2 ) => {
                if ( value1.label && value2.label ) {
                    if ( value1.label > value2.label ) {
                        return 1;
                    }
                    else if ( value1.label < value2.label ) {
                        return -1;
                    }
                }

                return 0;
            } );

            if ( referencesHasInvalid ) {
                referencesNodeClassNames.push( 'yn-invalid-children' );
            }
            else if ( referencesHasDirty ) {
                referencesNodeClassNames.push( 'yn-modified-children' );
            }
        }

        const treeData : TreeNode.TreeNode[] = [
            {
                key      : 'properties',
                label    : __( 'YN.base.finder.properties.label' ),
                children : [],
                className: propertiesPanelClassName,
                data     : {
                    tooltip: propertiesPanelTooltip
                }
            },
            {
                key       : 'enumProperties',
                label     : __( 'YN.base.finder.enumProperties.label' ),
                selectable: false,
                children  : enumPropertiesTreeData,

                // action node config
                className: 'yn-action-node',
                data     : {
                    showHideEmptyToggle   : true,
                    hideEmpty             : hideEmptyEnumProperties,
                    hideEmptyToggleHandler: this.onHideEmptyEnumProperties
                }
            },
            {
                key       : 'references',
                label     : __( 'YN.base.finder.references.label' ),
                selectable: false,
                children  : referencesTreeData,

                // action node config
                className: referencesNodeClassNames.join( ' ' ),
                data     : {
                    showHideEmptyToggle   : true,
                    hideEmpty             : hideEmptyReferences,
                    hideEmptyToggleHandler: this.onHideEmptyReferences
                }
            },
            {
                key     : 'graph',
                label   : __( 'YN.base.finder.graph.label' ),
                children: []
            }
        ];

        return (
            <Tree
                value={treeData}
                nodeTemplate={this.menuNodeTemplate}

                expandedKeys={menuExpandedKeys}
                onToggle={this.onMenuToggle}

                selectionMode="single"
                selectionKeys={menuSelectedKey}
                onSelectionChange={this.onMenuSelectionChange}

                className="yn-menu-tree"
            />
        );
    }

    getContent() : JSX.Element {
        const {
                  uid,
                  schema,
                  status,
                  timestamp,
                  timezone,
                  rootItemId,
                  rawMode,
                  showAdditionalRawNames,
                  menuSelectedKey,
                  deepLinkProps,
                  subPanelProps
              } = this.props as AllProps,
              {
                  item,
                  editable
              } = this.state;

        let retVal : JSX.Element = <></>,
            panelKey : string    = '',
            panelUid : string    = '',
            panelProps;

        if ( rootItemId && item ) {
            if ( menuSelectedKey === 'properties' ) {
                panelKey = FinderDetail.propertiesPanelKey;
                panelUid = `${uid}_${panelKey}`;
                panelProps = subPanelProps?.[ panelKey ] ?? {};

                // apply deepLinkProps if
                if ( deepLinkProps?.property ) {
                    panelProps = {
                        ...panelProps,
                        deepLinkProps
                    };
                }

                retVal = <PropertiesPanel
                    uid={panelUid}
                    dataContext={uid}

                    {...panelProps}

                    schema={schema}
                    status={status}
                    timestamp={timestamp}
                    timezone={timezone}

                    item={item}

                    rawMode={rawMode}
                    showAdditionalRawNames={showAdditionalRawNames}

                    editable={editable}
                />;
            }
            else if ( menuSelectedKey?.match( /^enumProperty/ ) ) {
                const propertyTypeName = FinderDetail.getPropertyTypeNameFromEnumPropertyKey( menuSelectedKey );

                if ( propertyTypeName ) {
                    panelKey = FinderDetail.getEnumPropertyKey( propertyTypeName );
                    panelUid = `${uid}_${panelKey}`;
                    panelProps = subPanelProps?.[ panelKey ] ?? {};

                    retVal = <EnumPropertyPanel
                        key={panelKey}

                        uid={panelUid}
                        dataContext={uid}

                        {...panelProps}

                        schema={schema}
                        status={status}
                        timestamp={timestamp}
                        timezone={timezone}

                        itemId={item.id}
                        itemStatusId={item.statusId}
                        enumProperty={item.enumProperties.get( metaModel[ schema ].getPropertyType( propertyTypeName )!.id )}
                        propertySet={metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( item.itemTypeId, propertyTypeName )}

                        rawMode={rawMode}
                        showAdditionalRawNames={showAdditionalRawNames}

                        editable={editable}
                    />;
                }
            }
            else if ( menuSelectedKey?.match( /^reference/ ) ) {
                const itemTypeName = FinderDetail.getItemTypeNameFromReferenceKey( menuSelectedKey );

                if ( itemTypeName ) {
                    panelKey = FinderDetail.getReferenceKey( itemTypeName );
                    panelUid = `${uid}_${panelKey}`;
                    panelProps = subPanelProps?.[ panelKey ] ?? {};

                    let setPanelProps = false;

                    // initialize panelProps if not
                    if ( !panelProps.parentItemId ) {
                        panelProps = this.initializeReferencePanelProps( { uid: panelUid, itemTypeName } );
                        setPanelProps = true;
                    }

                    // apply deepLinkProps if
                    if ( deepLinkProps?.referencedItemTypeName === itemTypeName && deepLinkProps.reference ) {
                        panelProps = {
                            ...panelProps,
                            deepLinkProps: deepLinkProps.reference
                        };
                        setPanelProps = true;

                        this.props.unsetParam( uid, 'deepLinkProps' );
                    }

                    if ( setPanelProps ) {
                        this.props.setParams( panelUid, panelProps );
                    }

                    retVal = <ReferencePanel
                        key={panelKey}

                        uid={panelUid}
                        dataContext={uid}

                        {...panelProps}

                        schema={schema}
                        status={status}
                        timestamp={timestamp}
                        timezone={timezone}

                        parentItemId={item.id}
                        parentItemTypeName={item.itemType.name}
                        itemTypeName={itemTypeName}

                        rawMode={rawMode}
                        showAdditionalRawNames={showAdditionalRawNames}

                        editable={!item.isDeleted()}
                    />;
                }
            }
            else if ( menuSelectedKey === 'graph' ) {
                panelKey = FinderDetail.graphPanelKey;
                panelUid = `${uid}_${panelKey}`;
                panelProps = subPanelProps?.[ panelKey ] ?? {};

                retVal = <div
                    id={`${panelUid}_wrapper`}
                    key={`${panelUid}_wrapper`}
                    className="yn-form-card yn-view-card yn-view-card-standalone"
                    style={{ display: 'flex', height: '100%', width: '100%' }}
                >
                    <Card
                        id={`${panelUid}_card`}
                        key={`${panelUid}_card`}
                        style={{ height: '100%', width: '100%' }}
                    >
                        <div
                            className="yn-view-wrapper yn-view-wrapper-graph yn-view-wrapper-card"
                        >
                            <div
                                id={`${panelUid}_border`}
                                key={`${panelUid}_border`}
                                className="yn-view-wrapper-border"
                            >
                                <LoadingMaskPanel
                                    key={`${panelUid}_loadingMask`}
                                    forComponent={panelUid}
                                    className="yn-view-loadingmask yn-graphview-loadingmask"
                                    style={{ width: '100%' }}
                                >
                                    <Graph
                                        uid={panelUid}
                                        dataContext={panelUid}
                                        schema={item.schema}
                                        graphType="force"
                                        type="data"
                                        isGenericGraph={true}
                                        additionalQueryParameters={{ rootItemId: item.id }}
                                        {...panelProps}
                                    />
                                </LoadingMaskPanel>
                            </div>
                        </div>
                    </Card>
                </div>;
            }
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  schema,
                  status,
                  timestamp,
                  timezone,
                  rawMode,
                  showAdditionalRawNames,
                  showHistoryPopup,
                  menuWidth,
                  menuExpandedKeys,
                  menuSelectedKey,
                  hideEmptyEnumProperties,
                  hideEmptyReferences,
                  allElusiveData,
                  elusiveData
              }                 = this.props as AllProps,
              {
                  item,
                  showDataNoLongerValidDialog,
                  errorCount,
                  errorTooltip,
                  errorMessage
              }                 = this.state,
              editableStatusIds = metaModel[ schema ].getEditableStatus().map( editStatus => editStatus.id ),
              isItemActive      = editableStatusIds.includes( item?.statusId ?? 0 );

        return <>
            <Dialog
                key={`${uid}_dialog`}
                header={__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.title' )}
                footer={<div><Button label="OK" className="p-button-outlined"
                                     onClick={() => this.props.setElusiveData( uid, { showCouldNotSavePopup: false } )}/></div>}
                maskClassName="yn-dialog-panel-modal"
                appendTo="self"
                position="top"
                visible={elusiveData?.showCouldNotSavePopup}
                style={{ width: 'auto', maxWidth: '80%' }}
                closable={false}
                className="yn-error-dialog"
                onHide={() => {
                }}>
                {errorMessage}
            </Dialog>
            <Dialog
                header={__( 'YN.base.finder.detail.dataNotValid.title' )}
                footer={<div>
                    <Button
                        key="okButton"
                        label={__( 'YN.base.common.ok' )}
                        className="p-button p-button-outlined"
                        onClick={() => {
                            this.props.closePanel( uid )
                        }}
                    />
                </div>}
                position="top"
                maskClassName="yn-dialog-panel-modal"
                appendTo="self"
                visible={showDataNoLongerValidDialog}
                closable={false}
                className="yn-warning-dialog"
                onHide={() => {
                    this.props.closePanel( uid )
                }}
                style={{ width: 'auto', maxWidth: '80%' }}
            >
                <p>{parse( __( 'YN.base.finder.detail.dataNotValid.text' ) )}</p>
            </Dialog>
            {item &&
                <ItemHistoryDialog
                    uid={`${uid}_history`}
                    schema={schema}
                    rootItemId={item.id}
                    visible={showHistoryPopup}
                    onHide={() => {
                        this.props.setParam( uid, 'showHistoryPopup', false );
                    }}
                />
            }
            <Headerbar
                key={`${uid}_ItemFinderHeaderbar`}

                uid={uid}

                schema={schema}
                status={status}
                timestamp={timestamp}
                timezone={timezone}

                item={item}

                rawMode={rawMode}
                showAdditionalRawNames={showAdditionalRawNames}

                showId={true}
                showType={true}
                showKey={true}
                showName={true}
                showStatus={true}
            />
            <Toolbar
                key={`${uid}_ItemFinderToolbar`}

                uid={uid}

                schema={schema}
                status={status}
                timestamp={timestamp}
                timezone={timezone}

                item={item}
                referenceCounts={elusiveData?.metaData?.referenceCounts}

                rawMode={rawMode}
                showAdditionalRawNames={showAdditionalRawNames}

                showHistoryPopup={showHistoryPopup}

                showSaveButton={true}
                saveButtonErrorCount={errorCount}
                saveButtonErrorTooltip={errorTooltip}
                showCloneButton={true}
                showRestoreButton={!isItemActive}
                showDeleteButton={isItemActive}
                showHistoryButton={true}
            />
            <LoadingMaskPanel forComponent={uid} className="yn-flex-layout-parent">
                <Toast
                    ref={( el ) => this.toast = el}
                    key={`${uid}_toast`}

                    position="bottom-right"
                    style={{ position: 'absolute' }}
                />
                <ResizeableLeftMenuPanel
                    key={`${uid}_menuContentPanel`}

                    uid={uid}

                    menuWidth={menuWidth}
                    minMenuWidth={15}
                    maxMenuWidth={30}
                    onMenuWidthChange={this.onMenuWidthChange}

                    menu={this.getMenu( schema, item, allElusiveData, elusiveData, menuExpandedKeys, menuSelectedKey, hideEmptyEnumProperties, hideEmptyReferences, rawMode, showAdditionalRawNames )}
                    content={this.getContent()}
                />
            </LoadingMaskPanel>
        </>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        allElusiveData  : undefined,
        elusiveData     : undefined,
        intercomMessages: undefined,
        user            : undefined
    };

    // get all errors for subPanels
    retVal.allElusiveData = state.elusiveData;

    // get elusiveData
    if ( state.elusiveData?.[ ownProps.uid ] ) {
        retVal.elusiveData = state.elusiveData[ ownProps.uid ];
    }

    // get intercom messages
    if ( state.intercom?.[ ownProps.uid ] ) {
        retVal.intercomMessages = state.intercom[ ownProps.uid ];
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect( mapStateToProps, mapDispatchToProps )( FinderDetail );
